<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const literatureMcqRoutes = [
  { title: "Unit 1: Khatam-un-Nabiyeen Hazrat Muhammad", to: "/11th/english/prose/khatam-un-nabiyeen-hazrat-muhammad/", detail: "Character, mission, and Farewell Sermon MCQ reasoning available." },
  { title: "Unit 2: Responsibility of the Youth in Nation-Building", to: "/11th/english/prose/responsibility-of-the-youth-in-nation-building/", detail: "Constructive spirit, freedom-with-discipline, and career-choice MCQ support." },
  { title: "Unit 4: Team Moon", to: "/11th/english/prose/team-moon/", detail: "Teamwork, hidden contributors, and mission-software MCQs now available." },
  { title: "Unit 5: Impact of Global Warming on Pakistan", to: "/11th/english/prose/impact-of-global-warming-on-pakistan/", detail: "Climate vulnerability, glacier impact, and adaptation MCQ reasoning available." },
  { title: "Unit 3: A Bird Came Down the Walk", to: "/11th/english/poems/a-bird-came-down-the-walk/", detail: "Nature observation and human-distance MCQ reasoning available." },
  { title: "Unit 6: The Echoing Green", to: "/11th/english/poems/the-echoing-green/", detail: "Poem-based MCQs on symbolism, tone, and life cycle now available." },
  { title: "Unit 7: What You Do is What You Are", to: "/11th/english/prose/what-you-do-is-what-you-are/", detail: "Identity, language, and social-status MCQ reasoning now available." },
  { title: "Unit 8: Clean Water", to: "/11th/english/prose/clean-water/", detail: "Pollution, conservation, and contextual-word MCQ reasoning available." },
  { title: "Unit 9: Freedom", to: "/11th/english/poems/freedom/", detail: "Freedom, dignity, and tone-based MCQs now available." },
  { title: "Unit 10: The Punishment of Shahpesh, the Persian, on Khipil, the Builder", to: "/11th/english/prose/the-punishment-of-shahpesh-the-persian-on-khipil-the-builder/", detail: "Power, punishment, and moral-justice MCQ reasoning now available." },
  { title: "Unit 11: Those Winter Sundays", to: "/11th/english/poems/those-winter-sundays/", detail: "Silent love and regret-based MCQs now available." },
  { title: "Unit 12: The Impact of AI on Society, Human Relationships, and Ethics", to: "/11th/english/prose/the-impact-of-ai-on-society-human-relationships-and-ethics/", detail: "Fullest MCQ coverage with option-by-option reasoning." },
  { title: "Unit 13: Ruba'iyat", to: "/11th/english/poems/rubaiyat/", detail: "Faith, unity, and spiritual-strength MCQs now available." },
  { title: "Unit 14: The End of the Beginning", to: "/11th/english/play/the-end-of-the-beginning/", detail: "Comedy, role-swap, and character-based MCQs now available." },
];

const grammarMcqRoutes = [
  { title: "Pair of Words", to: "/11th/english/grammar/pair-of-words/", detail: "Confusing-word MCQ with explanation." },
  { title: "Punctuation", to: "/11th/english/grammar/punctuation/", detail: "Correction-oriented objective prep support." },
];

const objectiveTips = [
  "Sab se pehle un units se start karo jahan option-by-option explanation available ho.",
  "Literature MCQ me chapter ka main theme ya key fact identify karna helpful hota hai.",
  "Grammar MCQ me wrong options bhi padho, kyun ke exam traps wahi se bante hain.",
];

const chapterWiseFocus = [
  "Nation-building chapter me key contrast yaad rakho: freedom with responsibility, not freedom without limits.",
  "Global warming chapter me cause, impact, aur response teen levels me socho.",
  "Poetry MCQ me symbol ya tone ko literal meaning se confuse na karo.",
];

const faqs = [
  {
    question: "What does the MCQs hub collect?",
    answer: "Yeh hub un literature aur grammar routes ko collect karta hai jahan MCQ reasoning ya option explanation available hai.",
  },
  {
    question: "Which units have the strongest MCQ coverage?",
    answer: "AI unit aur Team Moon jese routes stronger source-backed MCQ coverage rakhte hain, jab ke baqi routes broad objective support dete hain.",
  },
  {
    question: "Should I only read the correct option explanation?",
    answer: "Nahi. Wrong options bhi padho, kyun ke elimination skill objective paper me bohat useful hoti hai.",
  },
];

usePageSeo({
  title: "11th Class English MCQs | Chapter Wise Objective Prep with Answers",
  description: "11th class English MCQs ke liye prose, poems, play aur grammar routes with option-by-option explanations and objective prep support.",
  keywords: "11th class english mcqs, class 11 english mcqs, 1st year english mcqs, chapter wise english objective, english mcqs with answers",
  schemaType: "CollectionPage",
  schemaProperties: {
    mainEntity: {
      "@type": "ItemList",
      itemListElement: [...literatureMcqRoutes, ...grammarMcqRoutes].map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `https://ustaad.today${item.to}`,
      })),
    },
  },
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "MCQs", item: "/11th/english/mcqs/" },
  ],
});
</script>

<template>
  <PageContainer>
    <EnglishHero
      headline="Objective Prep"
      title="11th Class English MCQs"
      description="Yahan se aap literature aur grammar ke important MCQ pages khol sakte hain."
    />

    <EnglishSection
      title="Literature MCQ Routes"
      description="Literature ke MCQs ke liye ye chapters open karein."
    >
      <EnglishGrid>
        <EnglishCard
          v-for="item in literatureMcqRoutes"
          :key="item.to"
          :title="item.title"
          :description="item.detail"
          :to="item.to"
        />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection
      title="Grammar MCQ Routes"
      description="Grammar ke MCQs aur objective practice ke liye ye pages dekhain."
    >
      <EnglishGrid>
        <EnglishCard
          v-for="item in grammarMcqRoutes"
          :key="item.to"
          :title="item.title"
          :description="item.detail"
          :to="item.to"
        />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection
      title="Objective Prep Tips"
      description="MCQs solve karte waqt ye baatein yaad rakhein."
    >
      <EnglishGrid>
        <EnglishCard
          v-for="item in objectiveTips"
          :key="item"
          title="Tip"
          :description="item"
        />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection
      title="Chapter-Wise MCQ Focus"
      description="Objective paper me ye cheezen aksar poochi jati hain."
    >
      <EnglishGrid>
        <EnglishCard
          v-for="item in chapterWiseFocus"
          :key="item"
          title="Focus"
          :description="item"
        />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection
      title="FAQs"
      description="MCQs ke bare me aam sawalon ke jawab."
    >
      <EnglishGrid>
        <EnglishCard
          v-for="item in faqs"
          :key="item.question"
          :title="item.question"
          :description="item.answer"
        />
      </EnglishGrid>
    </EnglishSection>
  </PageContainer>
</template>
